import { useListState } from "@/hooks/use-list-state";
import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { rowNavHandlers } from "@/lib/row-nav";
import { apiRequest, parseErrorMessage, parseErrorStatus, parseErrorBody } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Users, GripVertical, LayoutList, Tag, Info } from "lucide-react";
import VendorOnboardingDialog from "@/components/vendor-onboarding-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Vendor, Property } from "@shared/schema";
import { VENDOR_ROLES, MAINTENANCE_SPECIALTIES, type MaintenanceSpecialty } from "@shared/schema";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";

const ROLE_LABELS: Record<string, string> = {
  cleaning: "Cleaning",
  maintenance: "Maintenance",
  property_manager: "Property Manager",
  other: "Other",
};

const SPECIALTY_LABELS: Record<string, string> = {
  electrical: "Electrical",
  hvac: "HVAC",
  handyman: "Handyman",
  plumbing: "Plumbing",
  landscaping: "Landscaping",
  pest_control: "Pest Control",
  roof_repair: "Roof Repair",
  laundry_repair: "Laundry Repair",
  painting: "Painting",
  appliance_repair: "Appliance Repair",
  locksmith: "Locksmith",
  flooring: "Flooring",
  drywall: "Drywall",
  carpentry: "Carpentry",
  window_repair: "Window Repair",
  garage_door: "Garage Door",
};

function SpecialtyBadges({ vendor }: { vendor: Vendor }) {
  const serviceTypes = Array.isArray(vendor.serviceTypes) ? vendor.serviceTypes as string[] : [];
  if (serviceTypes.length === 0) return <span className="text-gray-400 text-sm">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {serviceTypes.map((st) => (
        <Badge key={st} variant="secondary" className="text-xs">
          {SPECIALTY_LABELS[st] || st}
        </Badge>
      ))}
    </div>
  );
}

function VendorAvatar({ vendor }: { vendor: Vendor }) {
  const initials = vendor.firstName && vendor.lastName
    ? `${vendor.firstName[0]}${vendor.lastName[0]}`
    : String(vendor.name ?? "").split(" ").map(w => w[0]).join("").slice(0, 2);

  return (
    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600" data-testid={`avatar-vendor-${vendor.id}`}>
      {initials.toUpperCase()}
    </div>
  );
}

interface SortableVendorRowProps {
  vendor: Vendor;
  groupVendors: Vendor[];
  onNavigate: (id: number) => void;
  onGroupReorder: (newOrder: Vendor[]) => void;
  onDelete: (vendor: Vendor) => void;
  propertyMap: Map<number, Property>;
  displayRank: number;
}

function SortableVendorRow({ vendor, groupVendors, onNavigate, onGroupReorder, onDelete: _onDelete, propertyMap, displayRank }: SortableVendorRowProps) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: vendor.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [priorityInput, setPriorityInput] = useState(String(displayRank));

  useEffect(() => {
    setPriorityInput(String(displayRank));
  }, [displayRank]);

  const handlePriorityBlur = () => {
    if (readOnly) { setPriorityInput(String(displayRank)); return; }
    const num = parseInt(priorityInput, 10);
    if (!isNaN(num) && num > 0) {
      const without = groupVendors.filter((v) => v.id !== vendor.id);
      const insertAt = Math.min(num - 1, without.length);
      const newOrder = [...without.slice(0, insertAt), vendor, ...without.slice(insertAt)];
      setPriorityInput(String(insertAt + 1));
      onGroupReorder(newOrder);
    } else {
      setPriorityInput(String(displayRank));
    }
  };

  const propIds = Array.isArray(vendor.propertyIds) ? vendor.propertyIds as number[] : [];

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b hover:bg-gray-50 transition-colors"
      data-testid={`row-vendor-specialty-${vendor.id}`}
    >
      <td className="px-2 py-3 w-8">
        <button
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={readOnly}
          {...(readOnly ? {} : attributes)}
          {...(readOnly ? {} : listeners)}
          data-testid={`drag-handle-${vendor.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-2 py-3 w-20">
        <Input
          disabled={readOnly}
          type="number"
          min={1}
          max={groupVendors.length}
          value={priorityInput}
          onChange={(e) => setPriorityInput(e.target.value)}
          onBlur={handlePriorityBlur}
          className="w-16 h-7 text-sm text-center px-1"
          data-testid={`input-priority-${vendor.id}`}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td
        className="px-3 py-3 cursor-pointer"
        {...rowNavHandlers(`/app/operations/vendors/${vendor.id}`, () => onNavigate(vendor.id))}
      >
        <Link href={`/app/operations/vendors/${vendor.id}`} className="flex items-center gap-2 -mx-3 -my-3 px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <VendorAvatar vendor={vendor} />
          <span className="font-medium text-gray-900 text-sm" data-testid={`text-vendor-name-${vendor.id}`}>
            {vendor.name}
          </span>
          {(vendor as Vendor & { isLinked?: boolean }).isLinked && (
            <Badge variant="outline" className="text-xs text-gray-500" data-testid={`badge-linked-${vendor.id}`}>Linked</Badge>
          )}
        </Link>
      </td>
      <td className="px-3 py-3 text-sm text-gray-600">{vendor.businessName || "—"}</td>
      <td className="px-3 py-3">
        <div className="flex -space-x-2">
          {propIds.slice(0, 3).map((pid) => {
            const prop = propertyMap.get(pid);
            return (
              <div
                key={pid}
                className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-gray-600"
                title={prop?.name || `Property ${pid}`}
              >
                {(prop?.name || "P")[0]}
              </div>
            );
          })}
          {propIds.length > 3 && (
            <div className="w-7 h-7 rounded-full bg-brand-muted border-2 border-white flex items-center justify-center text-[10px] font-medium text-brand">
              +{propIds.length - 3}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-3">
        <SpecialtyBadges vendor={vendor} />
      </td>
    </tr>
  );
}

interface SpecialtyGroupProps {
  specialty: MaintenanceSpecialty;
  vendors: Vendor[];
  onReorder: (specialty: MaintenanceSpecialty, newOrder: Vendor[]) => void;
  onNavigate: (id: number) => void;
  onDelete: (vendor: Vendor) => void;
  propertyMap: Map<number, Property>;
}

function SpecialtyGroup({ specialty, vendors, onReorder, onNavigate, onDelete, propertyMap }: SpecialtyGroupProps) {
  const [localVendors, setLocalVendors] = useState<Vendor[]>(vendors);

  useEffect(() => {
    setLocalVendors(vendors);
  }, [vendors]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localVendors.findIndex((v) => v.id === active.id);
    const newIndex = localVendors.findIndex((v) => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(localVendors, oldIndex, newIndex);
    setLocalVendors(newOrder);
    onReorder(specialty, newOrder);
  }

  function handleGroupReorder(newOrder: Vendor[]) {
    setLocalVendors(newOrder);
    onReorder(specialty, newOrder);
  }

  if (localVendors.length === 0) return null;

  return (
    <div className="mb-6" data-testid={`group-specialty-${specialty}`}>
      <div className="flex items-center gap-2 mb-2">
        <Tag className="h-4 w-4 text-gray-400" />
        <h3 className="font-semibold text-gray-900 text-sm">{SPECIALTY_LABELS[specialty] || specialty}</h3>
        <Badge variant="secondary" className="text-xs">{localVendors.length}</Badge>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localVendors.map((v) => v.id)} strategy={verticalListSortingStrategy}>
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2 w-20">Priority</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Properties</th>
                  <th className="px-3 py-2">Specialties</th>
                </tr>
              </thead>
              <tbody>
                {localVendors.map((vendor, index) => (
                  <SortableVendorRow
                    key={vendor.id}
                    vendor={vendor}
                    groupVendors={localVendors}
                    displayRank={index + 1}
                    onNavigate={onNavigate}
                    onGroupReorder={handleGroupReorder}
                    onDelete={onDelete}
                    propertyMap={propertyMap}
                  />
                ))}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

export default function AppOperationsVendors() {
  const queryClient = useQueryClient();
  usePageTitle("Vendor Directory");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [search, setSearch] = useListState("vendors:search", "");
  const [roleFilter, setRoleFilter] = useLocalStorageState(
    "pp:vendors-role-filter",
    "",
  );
  const [propertyFilter, setPropertyFilter] = useLocalStorageState<string>(
    "pp:vendors-property-filter",
    "",
  );
  const [specialtyFilter, setSpecialtyFilter] = useLocalStorageState(
    "pp:vendors-specialty-filter",
    "",
  );
  const [viewMode, setViewMode] = useLocalStorageState<"list" | "specialty">(
    "pp:vendors-view-mode",
    "list",
    (stored) => (stored === "specialty" ? "specialty" : "list"),
  );
  const [addOpen, setAddOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [newVendorFirstName, setNewVendorFirstName] = useState("");
  const [newVendorLastName, setNewVendorLastName] = useState("");
  const [newVendorEmail, setNewVendorEmail] = useState("");
  const [newVendorPhone, setNewVendorPhone] = useState("");
  const [newVendorCompany, setNewVendorCompany] = useState("");
  const [newVendorMarketplaceBlocked, setNewVendorMarketplaceBlocked] = useState(false);
  const [phoneMatch, setPhoneMatch] = useState<{ id: number; name: string; businessName: string | null; category: string | null; phone: string | null } | null>(null);
  const [onboardingVendor, setOnboardingVendor] = useState<Vendor | null>(null);

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/vendors", search, roleFilter, propertyFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (roleFilter) params.set("role", roleFilter);
      if (propertyFilter) params.set("propertyId", propertyFilter);
      const qs = params.toString();
      const res = await fetch(`/api/app/operations/vendors${qs ? `?${qs}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch vendors");
      return res.json();
    },
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/app/operations/vendors", data);
      return res.json();
    },
    onSuccess: (createdVendor: Vendor) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      setAddOpen(false);
      setNewVendorName("");
      setNewVendorFirstName("");
      setNewVendorLastName("");
      setNewVendorEmail("");
      setNewVendorPhone("");
      setNewVendorCompany("");
      setNewVendorMarketplaceBlocked(false);
      setPhoneMatch(null);
      toast({ title: "Vendor added successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/getting-started"] });
      // Open the onboarding dialog so the host sets roles, specialties, and
      // preferred properties before they invite the vendor — otherwise the
      // vendor logs in and sees nothing.
      if (createdVendor && typeof createdVendor.id === "number") {
        setOnboardingVendor(createdVendor);
      }
    },
    onError: (e: any) => {
      // Task #4449 — a duplicate phone returns a structured 409 with the
      // existing vendor's public fields. Instead of a generic error toast we
      // render an inline match card offering a one-click "Add" to the roster.
      if (parseErrorStatus(e) === 409) {
        const body = parseErrorBody(e);
        const existing = body?.existingVendor;
        if (existing && typeof existing.id === "number") {
          setPhoneMatch({
            id: existing.id,
            name: existing.name ?? "",
            businessName: existing.businessName ?? null,
            category: existing.category ?? null,
            phone: existing.phone ?? null,
          });
          return;
        }
      }
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't add this vendor. Please try again."), variant: "destructive" });
    },
  });

  const addExistingMutation = useMutation({
    mutationFn: async (vendorId: number) => {
      // `source: "phone_match"` records that this link came from the
      // duplicate-phone card rather than marketplace browsing.
      const res = await apiRequest("POST", `/api/app/operations/marketplace/${vendorId}/add`, { source: "phone_match" });
      return res.json();
    },
    onSuccess: (data: { vendor?: Vendor | null }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/getting-started"] });
      setAddOpen(false);
      setNewVendorName("");
      setNewVendorFirstName("");
      setNewVendorLastName("");
      setNewVendorEmail("");
      setNewVendorPhone("");
      setNewVendorCompany("");
      setNewVendorMarketplaceBlocked(false);
      setPhoneMatch(null);
      toast({ title: "Vendor added to your roster" });
      // Same as the create path: continue to the role / properties step so
      // the linked vendor is grouped and property-scoped correctly.
      if (data?.vendor && typeof data.vendor.id === "number") {
        setOnboardingVendor(data.vendor);
      }
    },
    onError: async (e: any, vendorId: number) => {
      const status = parseErrorStatus(e);
      if (status === 409) {
        // Already linked (e.g. an earlier Add that appeared to do nothing).
        // Open the role/properties step for the existing link instead of a
        // dead-end toast so the user can finish setting the vendor up.
        try {
          const res = await apiRequest("GET", `/api/app/operations/vendors/${vendorId}`);
          const existing = await res.json();
          if (existing && typeof existing.id === "number") {
            setAddOpen(false);
            setPhoneMatch(null);
            toast({ title: "Already on your roster", description: "Review their role and properties below." });
            setOnboardingVendor(existing);
            return;
          }
        } catch {
          // fall through to the plain toast
        }
        toast({ title: "Already on your roster", description: "This vendor is already on your roster.", variant: "destructive" });
        return;
      }
      if (status === 404) {
        toast({ title: "Vendor not found", description: "This vendor could not be found. It may have been removed.", variant: "destructive" });
        return;
      }
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't add this vendor to your roster. Please try again."), variant: "destructive" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (groupVendorIds: number[]) => {
      await apiRequest("POST", "/api/app/operations/vendors/reorder", { groupVendorIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
    },
    onError: (e: any) => {
      toast({ title: "Error saving order", description: parseErrorMessage(e, "Couldn't save the new vendor order. Please try again."), variant: "destructive" });
    },
  });

  const handleAdd = () => {
    const displayName = newVendorFirstName && newVendorLastName
      ? `${newVendorFirstName} ${newVendorLastName}`
      : newVendorName;
    if (!displayName.trim()) return;
    setPhoneMatch(null);
    const existingPriorities = (vendorsQuery.data || [])
      .map((v) => v.priority)
      .filter((p): p is number => p != null);
    const nextPriority = existingPriorities.length > 0 ? Math.max(...existingPriorities) + 1 : 1;
    createMutation.mutate({
      name: displayName,
      firstName: newVendorFirstName || null,
      lastName: newVendorLastName || null,
      email: newVendorEmail || null,
      phone: newVendorPhone || null,
      businessName: newVendorCompany || null,
      category: "other",
      priority: nextPriority,
      isMarketplaceBlocked: newVendorMarketplaceBlocked,
    });
  };

  const handleReorder = (_specialty: MaintenanceSpecialty, newOrder: Vendor[]) => {
    const groupVendorIds = newOrder.map((vendor) => vendor.id);
    reorderMutation.mutate(groupVendorIds);
  };

  const handleDelete = async (vendor: Vendor) => {
    await apiRequest("DELETE", `/api/app/operations/vendors/${vendor.id}`);
    queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
    toast({ title: "Vendor deleted" });
  };

  const vendors = vendorsQuery.data || [];
  const properties = propertiesQuery.data || [];
  const propertyMap = new Map(properties.map(p => [p.id, p]));

  // Client-side specialty filter applied after server-side role/property filters.
  // Treat "" and "all" as "no filter" (matches the convention used by role/property filters).
  const activeSpecialtyFilter = specialtyFilter && specialtyFilter !== "all" ? specialtyFilter : "";
  const specialtyFilteredVendors = activeSpecialtyFilter
    ? vendors.filter((v) => {
        const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes as string[] : [];
        return serviceTypes.includes(activeSpecialtyFilter);
      })
    : vendors;

  const specialtyGroups = MAINTENANCE_SPECIALTIES.map((spec) => ({
    specialty: spec,
    vendors: specialtyFilteredVendors
      .filter((v) => {
        const roles = Array.isArray(v.roles) ? v.roles as string[] : [];
        const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes as string[] : [];
        return roles.includes("maintenance") && serviceTypes.includes(spec);
      })
      .slice()
      .sort((a, b) => {
        const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
        const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
        if (pa !== pb) return pa - pb;
        return (a.name || "").localeCompare(b.name || "");
      }),
  })).filter((g) => g.vendors.length > 0);

  const unspecializedVendors = specialtyFilteredVendors.filter((v) => {
    const roles = Array.isArray(v.roles) ? v.roles as string[] : [];
    const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes as string[] : [];
    const isMaintenanceWithSpecialty = roles.includes("maintenance") && serviceTypes.some((st) =>
      (MAINTENANCE_SPECIALTIES as readonly string[]).includes(st)
    );
    return !isMaintenanceWithSpecialty;
  }).slice().sort((a, b) => {
    const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
    const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="page-operations-vendors">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-vendors-title" data-tour="vendors-heading">Vendors</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage your vendor directory. Add service providers, assign roles, and track preferred properties.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="vendors" />
            <GuideButton pageKey="vendors" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="vendors" />

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 sm:flex-wrap">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
            <div className="relative flex-1 sm:max-w-sm min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-vendors"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="select-role-filter" data-tour="vendors-categories">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {VENDOR_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SearchableSelect
              value={propertyFilter}
              onValueChange={setPropertyFilter}
              placeholder="All properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId="select-property-filter"
              className="w-full sm:w-[200px]"
              options={[
                { value: "all", label: "All properties" },
                ...properties.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                  keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                })),
              ]}
            />
            <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="select-specialty-filter">
                <SelectValue placeholder="All specialties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All specialties</SelectItem>
                {MAINTENANCE_SPECIALTIES.map((s) => (
                  <SelectItem key={s} value={s}>{SPECIALTY_LABELS[s] || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center border rounded-md overflow-hidden">
              <button
                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${viewMode === "list" ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-500 hover:bg-gray-50"}`}
                onClick={() => setViewMode("list")}
                data-testid="button-view-list"
              >
                <LayoutList className="h-4 w-4" />
                List
              </button>
              <button
                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors border-l ${viewMode === "specialty" ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-500 hover:bg-gray-50"}`}
                onClick={() => setViewMode("specialty")}
                data-testid="button-view-specialty"
              >
                <Tag className="h-4 w-4" />
                By Specialty
              </button>
            </div>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button disabled={readOnly} className="bg-brand hover:bg-brand-hover text-white" data-testid="button-add-vendor" data-tour="vendors-invite">
                  <Plus className="h-4 w-4 mr-1" />
                  Add new
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add new vendor</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>First name</Label>
                      <Input
                        value={newVendorFirstName}
                        onChange={(e) => setNewVendorFirstName(e.target.value)}
                        placeholder="First name"
                        data-testid="input-vendor-first-name"
                      />
                    </div>
                    <div>
                      <Label>Last name</Label>
                      <Input
                        value={newVendorLastName}
                        onChange={(e) => setNewVendorLastName(e.target.value)}
                        placeholder="Last name"
                        data-testid="input-vendor-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={newVendorCompany}
                      onChange={(e) => setNewVendorCompany(e.target.value)}
                      placeholder="Company name"
                      data-testid="input-vendor-company"
                    />
                  </div>
                  <div>
                    <Label>
                      Phone <span className="text-destructive" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      type="tel"
                      value={newVendorPhone}
                      onChange={(e) => setNewVendorPhone(e.target.value)}
                      placeholder="+1 (404) 555-1234"
                      required
                      aria-required="true"
                      data-testid="input-vendor-phone"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newVendorEmail}
                      onChange={(e) => setNewVendorEmail(e.target.value)}
                      placeholder="email@example.com"
                      data-testid="input-vendor-email"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="vendor-marketplace-blocked"
                      checked={newVendorMarketplaceBlocked}
                      onCheckedChange={(checked) => setNewVendorMarketplaceBlocked(checked === true)}
                      className="mt-0.5"
                      data-testid="checkbox-vendor-marketplace-blocked"
                    />
                    <Label
                      htmlFor="vendor-marketplace-blocked"
                      className="flex items-center gap-1.5 text-sm font-normal leading-snug cursor-pointer"
                    >
                      Block vendor from being shown in the marketplace
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-gray-400 shrink-0" data-testid="tooltip-vendor-marketplace-blocked" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            Vendor will only be able to be added by someone directly who already has their phone number.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                  </div>
                  {phoneMatch && (
                    <div className="rounded-lg border border-brand/40 bg-brand-light p-3" data-testid="card-phone-match">
                      <p className="text-sm font-medium text-gray-800">
                        A vendor with this phone number already exists
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        You can add them to your roster instead of creating a duplicate.
                      </p>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate" data-testid="text-phone-match-name">
                            {phoneMatch.businessName || phoneMatch.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate" data-testid="text-phone-match-detail">
                            {[phoneMatch.businessName ? phoneMatch.name : null, phoneMatch.category, phoneMatch.phone]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addExistingMutation.mutate(phoneMatch.id)}
                          disabled={addExistingMutation.isPending}
                          className="bg-brand hover:bg-brand-hover text-white shrink-0"
                          data-testid="button-add-existing-vendor"
                        >
                          {addExistingMutation.isPending ? "Adding..." : "Add"}
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={handleAdd}
                    disabled={readOnly || createMutation.isPending || (!newVendorFirstName && !newVendorLastName && !newVendorName) || !newVendorPhone.trim()}
                    data-testid="button-submit-vendor"
                  >
                    {createMutation.isPending ? "Adding..." : "Add vendor"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {vendorsQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="text-no-vendors">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium text-gray-700">No vendors yet</p>
            <p className="text-sm mt-1">Add your first vendor to get started managing your service providers.</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="table-vendors" data-tour="vendors-list">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 w-10">Pic</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Preferred properties</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Specialties</th>
                </tr>
              </thead>
              <tbody>
                {specialtyFilteredVendors.map((vendor) => {
                  const roles = Array.isArray(vendor.roles) ? vendor.roles as string[] : [];
                  const propIds = Array.isArray(vendor.propertyIds) ? vendor.propertyIds as number[] : [];
                  return (
                    <tr
                      key={vendor.id}
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      {...rowNavHandlers(`/app/operations/vendors/${vendor.id}`, navigate)}
                      data-testid={`row-vendor-${vendor.id}`}
                    >
                      <td className="px-4 py-3">
                        <VendorAvatar vendor={vendor} />
                      </td>
                      <td className="px-4 py-3">
                        <Link href={`/app/operations/vendors/${vendor.id}`} className="flex items-center gap-2 -mx-4 -my-3 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <span className="font-medium text-gray-900" data-testid={`text-vendor-name-${vendor.id}`}>
                            {vendor.name}
                          </span>
                          {vendor.priority != null && (
                            <Badge variant="outline" className="text-xs text-gray-400">#{vendor.priority}</Badge>
                          )}
                          {(vendor as Vendor & { isLinked?: boolean }).isLinked && (
                            <Badge variant="outline" className="text-xs text-gray-500" data-testid={`badge-linked-${vendor.id}`}>Linked</Badge>
                          )}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {vendor.businessName || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex -space-x-2">
                          {propIds.slice(0, 3).map((pid) => {
                            const prop = propertyMap.get(pid);
                            return (
                              <div
                                key={pid}
                                className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-gray-600"
                                title={prop?.name || `Property ${pid}`}
                              >
                                {(prop?.name || "P")[0]}
                              </div>
                            );
                          })}
                          {propIds.length > 3 && (
                            <div className="w-8 h-8 rounded-full bg-brand-muted border-2 border-white flex items-center justify-center text-[10px] font-medium text-brand">
                              +{propIds.length - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {roles.length === 0 ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {roles.map((r) => (
                              <Badge key={r} variant="secondary" className="text-xs">
                                {ROLE_LABELS[r] || r}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <SpecialtyBadges vendor={vendor} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div data-testid="view-specialty">
            <p className="text-sm text-gray-500 mb-4">
              Drag rows or type a number to reorder vendors within each specialty group. Lower numbers appear first in task assignment dropdowns.
            </p>
            {specialtyGroups.map(({ specialty, vendors: groupVendors }) => (
              <SpecialtyGroup
                key={specialty}
                specialty={specialty}
                vendors={groupVendors}
                onReorder={handleReorder}
                onNavigate={(id) => navigate(`/app/operations/vendors/${id}`)}
                onDelete={handleDelete}
                propertyMap={propertyMap}
              />
            ))}
            {unspecializedVendors.length > 0 && (
              <div className="mb-6" data-testid="group-specialty-other">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <h3 className="font-semibold text-gray-900 text-sm">Other / No Specialty</h3>
                  <Badge variant="secondary" className="text-xs">{unspecializedVendors.length}</Badge>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b text-left text-xs text-gray-500 uppercase tracking-wider">
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Company</th>
                        <th className="px-3 py-2">Specialties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unspecializedVendors.map((vendor) => {
                        return (
                          <tr
                            key={vendor.id}
                            className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                            {...rowNavHandlers(`/app/operations/vendors/${vendor.id}`, navigate)}
                            data-testid={`row-vendor-other-${vendor.id}`}
                          >
                            <td className="px-3 py-3">
                              <Link href={`/app/operations/vendors/${vendor.id}`} className="flex items-center gap-2 -mx-3 -my-3 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                <VendorAvatar vendor={vendor} />
                                <span className="font-medium text-gray-900 text-sm">{vendor.name}</span>
                              </Link>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">{vendor.businessName || "—"}</td>
                            <td className="px-3 py-3">
                              <SpecialtyBadges vendor={vendor} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {specialtyGroups.length === 0 && unspecializedVendors.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">
                No vendors with specialty tags. Add maintenance specialties to vendors to see them grouped here.
              </div>
            )}
          </div>
        )}
      </div>
      <VendorOnboardingDialog
        vendor={onboardingVendor}
        open={onboardingVendor !== null}
        onClose={() => setOnboardingVendor(null)}
      />
    </AppLayout>
  );
}
